import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_CONFIG, GENEVA_BOUNDS, GENEVA_CENTER } from '../config/googleMapsConfig';

interface GooglePlaceResult {
  place_id: string;
  display_name: string;
  lat: number;
  lon: number;
  label: string;
}

class GoogleMapsService {
  private loader: Loader;
  private placesService?: google.maps.places.PlacesService;
  private autocompleteService?: google.maps.places.AutocompleteService;
  private geocoder?: google.maps.Geocoder;

  constructor() {
    this.loader = new Loader({
      apiKey: GOOGLE_MAPS_CONFIG.apiKey,
      version: GOOGLE_MAPS_CONFIG.version,
      libraries: GOOGLE_MAPS_CONFIG.libraries,
      region: GOOGLE_MAPS_CONFIG.region,
      language: GOOGLE_MAPS_CONFIG.language,
    });
  }

  async initialize(): Promise<void> {
    try {
      console.log('Google Maps Service: Loading Google Maps API...');
      const google = await this.loader.load();
      console.log('Google Maps Service: API loaded successfully');
      
      // Create a dummy element for PlacesService
      const dummyElement = document.createElement('div');
      this.placesService = new google.maps.places.PlacesService(dummyElement);
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.geocoder = new google.maps.Geocoder();
      
      console.log('Google Maps Service: Services initialized successfully');
    } catch (error) {
      console.error('Google Maps Service: Error loading Google Maps API:', error);
      throw error;
    }
  }

  async searchAddress(query: string): Promise<GooglePlaceResult[]> {
    console.log('Google Maps Service: Searching for:', query);
    
    if (!this.autocompleteService || !this.geocoder) {
      console.log('Google Maps Service: Initializing...');
      await this.initialize();
    }

    // We need to make separate requests for different types since they can't be mixed
    const searchRequests = [
      this.searchByType(query, ['address']),
      this.searchByType(query, ['establishment']),
      this.searchByType(query, ['point_of_interest'])
    ];

    try {
      const results = await Promise.all(searchRequests);
      const allResults = results.flat();
      
      // Remove duplicates based on place_id
      const uniqueResults = allResults.filter((result, index, self) => 
        index === self.findIndex(r => r.place_id === result.place_id)
      );
      
      console.log('Google Maps Service: Combined results:', uniqueResults.length);
      return uniqueResults;
    } catch (error) {
      console.error('Google Maps Service: Error in combined search:', error);
      return [];
    }
  }

  private async searchByType(query: string, types: string[]): Promise<GooglePlaceResult[]> {
    return new Promise((resolve) => {
      if (!this.autocompleteService) {
        resolve([]);
        return;
      }

      console.log('Google Maps Service: Making autocomplete request for types:', types);
      this.autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'ch' }, // Restrict to Switzerland
          types: types,
          // Use new locationRestriction instead of bounds
          locationRestriction: new google.maps.LatLngBounds(
            new google.maps.LatLng(GENEVA_BOUNDS.southwest.lat, GENEVA_BOUNDS.southwest.lng),
            new google.maps.LatLng(GENEVA_BOUNDS.northeast.lat, GENEVA_BOUNDS.northeast.lng)
          ),
          // Use new locationBias instead of location + radius
          locationBias: new google.maps.LatLng(GENEVA_CENTER.lat, GENEVA_CENTER.lng),
        },
        async (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
          console.log(`Google Maps Service: Autocomplete response for ${types}:`, { status, predictions });
          
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            try {
              const basicResults: GooglePlaceResult[] = predictions.map(prediction => ({
                place_id: prediction.place_id,
                display_name: prediction.description,
                lat: 0, // Will be set when user selects
                lon: 0, // Will be set when user selects
                label: prediction.description,
              }));
              
              console.log(`Google Maps Service: Returning ${basicResults.length} results for ${types}`);
              resolve(basicResults);
            } catch (error) {
              console.error(`Google Maps Service: Error processing predictions for ${types}:`, error);
              resolve([]);
            }
          } else {
            console.log(`Google Maps Service: No predictions or error status for ${types}:`, status);
            resolve([]);
          }
        }
      );
    });
  }

  async getPlaceDetails(placeId: string): Promise<GooglePlaceResult | null> {
    if (!this.placesService) {
      await this.initialize();
    }
    
    return new Promise((resolve) => {
      if (!this.placesService) {
        console.error('Google Maps Service: PlacesService not available for getPlaceDetails');
        resolve(null);
        return;
      }

      console.log('Google Maps Service: Getting details for place:', placeId);
      this.placesService.getDetails(
        {
          placeId: placeId,
          fields: ['place_id', 'formatted_address', 'geometry', 'name'],
        },
        (place: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
          console.log('Google Maps Service: Place details response:', { status, place });
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const result: GooglePlaceResult = {
              place_id: place.place_id || '',
              display_name: place.formatted_address || '',
              lat: place.geometry?.location?.lat() || 0,
              lon: place.geometry?.location?.lng() || 0,
              label: place.formatted_address || '',
            };
            console.log('Google Maps Service: Returning place details:', result);
            resolve(result);
          } else {
            console.log('Google Maps Service: Failed to get place details, status:', status);
            resolve(null);
          }
        }
      );
    });
  }
}

const googleMapsService = new GoogleMapsService();
export default googleMapsService; 